import { InsightsStateSelector } from 'app-redux/InsightsState';
import { RiskLikelihood, RiskMagnitude } from 'models/RiskCategories';
import { getRiskLikelihood, getRiskMagnitude } from 'utils/modelHelpers';
import { createSelector } from '@reduxjs/toolkit';
import { orderBy } from 'lodash';
import * as dto from 'client/dto';
import { calculateToWholePercent } from 'utils/mathHelpers';

const getReportedCaseCount: (diseaseId: number) => InsightsStateSelector<number> = diseaseId =>
  createSelector(
    [state => state.diseaseActivitiesRelevant.caseCountsByDiseaseId],
    caseCountsByDiseaseId => caseCountsByDiseaseId[diseaseId]?.reportedCases
  );
const getExportationRiskMagnitude: (diseaseId: number) => InsightsStateSelector<RiskMagnitude> = diseaseId =>
  createSelector([state => state.diseaseActivitiesRelevant.exportRisksByDiseaseId], exportRisksByDiseaseId =>
    getRiskMagnitude(exportRisksByDiseaseId[diseaseId])
  );
const getCaseCountPrevalenceForLocation: (diseaseId: number, locationId: number) => InsightsStateSelector<number> = (diseaseId, locationId) =>
  createSelector(
    [state => state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId],
    entriesByDiseaseIdAndLocationId =>
      entriesByDiseaseIdAndLocationId[diseaseId] && entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.casesPerPopulation
  );
const getAllFilteredLocationIds: InsightsStateSelector<number[]> = createSelector(
  state => state.diseaseActivitiesRelevant.locationNamesById,
  state => state.diseaseActivitiesRelevant.filteredLocationIds,
  (locationNamesById, filteredLocationIds) =>
    orderBy(
      filteredLocationIds.filter(x => Object.keys(locationNamesById).includes(`${x}`)),
      x => locationNamesById[x]
    )
);
const getAllFilteredDiseaseIds: InsightsStateSelector<number[]> = createSelector(
  state => state.diseaseActivitiesRelevant.diseaseNamesById,
  state => state.diseaseActivitiesRelevant.filteredDiseaseIds,
  (diseaseNamesById, filteredDiseaseIds) =>
    orderBy(
      filteredDiseaseIds.filter(x => Object.keys(diseaseNamesById).includes(`${x}`)),
      x => diseaseNamesById[x]
    )
);
const getAllSortedFilteredLocationIds: InsightsStateSelector<number[]> = createSelector(
  state => state.diseaseActivitiesRelevant.sortedLocationIds,
  getAllFilteredLocationIds,
  (sortedIds, filteredIds) => (filteredIds.length ? sortedIds.filter(x => filteredIds.includes(x)) : sortedIds)
);
const getAllSortedFilteredDiseaseIds: InsightsStateSelector<number[]> = createSelector(
  state => state.diseaseActivitiesRelevant.sortedDiseaseIds,
  getAllFilteredDiseaseIds,
  (sortedIds, filteredIds) => (filteredIds.length ? sortedIds.filter(x => filteredIds.includes(x)) : sortedIds)
);
const getIsCaseCountPrevalenceForLocationInherited: (diseaseId: number, locationId: number) => InsightsStateSelector<boolean> = (
  diseaseId,
  locationId
) =>
  createSelector(
    [state => state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId],
    entriesByDiseaseIdAndLocationId =>
      entriesByDiseaseIdAndLocationId[diseaseId] && entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.isInherited
  );
const getHasCaseCountDataForLocation: (diseaseId: number, locationId: number) => InsightsStateSelector<boolean> = (diseaseId, locationId) =>
  createSelector(
    [state => state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId],
    entriesByDiseaseIdAndLocationId =>
      entriesByDiseaseIdAndLocationId[diseaseId] && entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.hasReportedData
  );
const getLocationRowsSortBy: InsightsStateSelector<{
  diseaseId: number;
  sortBy: dto.RiskFeedSortType;
}> = createSelector([state => state.diseaseActivitiesRelevant.locationIdsSortBy], locationIdsSortBy => locationIdsSortBy);
const getDiseaseColumnsSortBy: InsightsStateSelector<{
  locationId: number;
  sortBy: dto.RiskFeedSortType;
}> = createSelector([state => state.diseaseActivitiesRelevant.diseaseIdsSortBy], diseaseIdsSortBy => diseaseIdsSortBy);
const getSortBy: InsightsStateSelector<dto.RiskFeedSortType> = state => state.diseaseActivitiesRelevant.sortBy;
const isCustomSort: InsightsStateSelector<boolean> = state =>
  state.diseaseActivitiesRelevant.diseaseIdsSortBy !== null || state.diseaseActivitiesRelevant.locationIdsSortBy !== null;
const getImportationRiskMagnitude: (diseaseId: number, locationId: number) => InsightsStateSelector<RiskMagnitude> =
  (diseaseId, locationId) => state =>
    state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId]
      ? getRiskMagnitude(state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.importationRisk)
      : null;
const isSortOnDiseaseActive: (diseaseId: number) => InsightsStateSelector<boolean> = diseaseId => state =>
  state.diseaseActivitiesRelevant.locationIdsSortBy?.diseaseId === diseaseId;
const isSortOnLocationActive: (locationId: number) => InsightsStateSelector<boolean> = locationId => state =>
  state.diseaseActivitiesRelevant.diseaseIdsSortBy?.locationId === locationId;
const getImportationRiskLikelihood: (diseaseId: number, locationId: number) => InsightsStateSelector<RiskLikelihood> =
  (diseaseId, locationId) => state =>
    state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId]
      ? getRiskLikelihood(state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.importationRisk)
      : 'Not calculated';
const isSortResetButtonEnabled: InsightsStateSelector<boolean> = state => getSortBy(state) !== dto.RiskFeedSortType.MostAtRisk || isCustomSort(state);
const isFilterResetButtonEnabled: InsightsStateSelector<boolean> = state =>
  !!getAllFilteredLocationIds(state).length || !!getAllFilteredDiseaseIds(state).length;
const getParentLocationName: (diseaseId: number, locationId: number) => InsightsStateSelector<string> = (diseaseId, locationId) => state =>
  state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId] &&
  state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.hasReportedData &&
  state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.isInherited
    ? state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.parentLocationName
    : '';
const getParentLocationReportedCaseCount: (diseaseId: number, locationId: number) => InsightsStateSelector<number> =
  (diseaseId, locationId) => state =>
    state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId] &&
    state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.hasReportedData &&
    state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.isInherited
      ? state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.parentLocationReportedCases
      : 0;
const getLocationReportedCaseCount: (diseaseId: number, locationId: number) => InsightsStateSelector<number> = (diseaseId, locationId) => state =>
  state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId] &&
  state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.hasReportedData
    ? state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId[diseaseId][locationId]?.reportedCases
    : 0;
const isLoading: InsightsStateSelector<boolean> = state => state.diseaseActivitiesRelevant.isLoading;
const isLegendDisplayed: InsightsStateSelector<boolean> = state => state.diseaseActivitiesRelevant.isLegendDisplayed;
const getActiveDiseaseIdSort: InsightsStateSelector<number> = state => state.diseaseActivitiesRelevant.locationIdsSortBy?.diseaseId;
const getActiveLocationIdSort: InsightsStateSelector<number> = state => state.diseaseActivitiesRelevant.diseaseIdsSortBy?.locationId;
const hasPanelData: (diseaseId: number, locationId?: number) => InsightsStateSelector<boolean> = (diseaseId, locationId) =>
  createSelector(
    [
      state => state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId,
      state => state.diseaseActivitiesRelevant.caseCountsByDiseaseId,
      state => state.diseaseActivitiesRelevant.exportRisksByDiseaseId,
    ],
    (entriesByDiseaseIdAndLocationId, globalDiseaseCaseCount, globalExportationRisk) => {
      const diseaseLocationData = entriesByDiseaseIdAndLocationId[diseaseId] && entriesByDiseaseIdAndLocationId[diseaseId][locationId];
      const hasCases = locationId
        ? diseaseLocationData?.hasReportedData && !!diseaseLocationData.casesPerPopulation
        : !!globalDiseaseCaseCount[diseaseId]?.reportedCases;
      const hasRelevantRisk =
        ((locationId ? diseaseLocationData?.importationRisk?.maxProbability : globalExportationRisk[diseaseId]?.maxProbability) ?? 0) >= 0.01;

      return hasCases || hasRelevantRisk;
    }
  );
const getDisruptionRisk: (diseaseId: number, locationId: number) => InsightsStateSelector<dto.DisruptionModel> = (diseaseId, locationId) =>
  createSelector([state => state.diseaseActivitiesRelevant.entriesByDiseaseIdAndLocationId], entriesByDiseaseIdAndLocationId => {
    const disruptionModel = entriesByDiseaseIdAndLocationId[diseaseId]?.[locationId]?.disruption;

    if (!disruptionModel) return null;

    return {
      ...disruptionModel,
      locationResiliency: calculateToWholePercent(disruptionModel?.locationResiliency),
    };
  });

export default {
  getReportedCaseCount,
  getExportationRiskMagnitude,
  getImportationRiskLikelihood,
  getImportationRiskMagnitude,
  getCaseCountPrevalenceForLocation,
  getParentLocationName,
  getParentLocationReportedCaseCount,
  getLocationReportedCaseCount,
  getIsCaseCountPrevalenceForLocationInherited,
  getHasCaseCountDataForLocation,
  getAllFilteredLocationIds,
  getAllFilteredDiseaseIds,
  getAllSortedFilteredLocationIds,
  getAllSortedFilteredDiseaseIds,
  getLocationRowsSortBy,
  getDiseaseColumnsSortBy,
  getSortBy,
  isSortOnDiseaseActive,
  isSortOnLocationActive,
  isCustomSort,
  isSortResetButtonEnabled,
  isFilterResetButtonEnabled,
  isLoading,
  isLegendDisplayed,
  getActiveDiseaseIdSort,
  getActiveLocationIdSort,
  hasPanelData,
  getDisruptionRisk,
};
